import { ChangeDetectionStrategy, Component, signal, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { WorkflowDetailsTableViewComponent } from './workflow-details-table-view/workflow-details-table-view.component';
import {
  WorkflowPermissionDetailsAssigneeTabList,
  WorkflowPermissionTable,
} from '../../workflow.model';
import { workflowDetailsInitialData } from '../../workflow.constants';
import { WorkflowService } from '../../workflow.service';
import { WorkflowDetailsTableAddMemberComponent } from './workflow-details-table-add-member/workflow-details-table-add-member.component';
import { WorkflowDetailsAssigneeTableViewComponent } from './workflow-details-asignee-table-view/workflow-details-assignee-table-view.component';
import { AddIconComponent } from '@kuiper/icons';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { TabsComponent } from '../../../../../../ui-components/lib/tabs/tabs.component';
import { TabComponent } from '../../../../../../ui-components/lib/tabs/tab/tab.component';
import { Meta } from '../../../../../common/models/api-response.model';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { RequestDetailsHeaderComponent } from '../../../../../common/components/request-details-header/request-details-header.component';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../../common/constants/pagination.constants';
import { VIEW_MODE } from '../../../../../common/constants/app-permissions';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { RequestDetailsResponse } from '../../../../../common/models/request-details.model';

@Component({
  selector: 'lib-workflow-permission-details',
  imports: [
    RequestDetailsHeaderComponent,
    BadgeComponent,
    TranslocoPipe,
    PaginatorComponent,
    WorkflowDetailsTableViewComponent,
    WorkflowDetailsTableAddMemberComponent,
    RouterLink,
    OverlayLoaderComponent,
    TabsComponent,
    TabComponent,
    WorkflowDetailsAssigneeTableViewComponent,
    AddIconComponent,
    TenantPathPipe,
  ],
  templateUrl: './workflow-permission-details.component.html',
  styleUrl: './workflow-permission-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowPermissionDetailsComponent implements OnInit {
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly VIEW_MODE = VIEW_MODE;
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  route = inject(ActivatedRoute);
  router = inject(Router);
  workflowService = inject(WorkflowService);
  tenantService = inject(TenantService);

  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(20);
  isLoading = signal<boolean>(true);
  totalMembersCount = signal<string>('');
  mode = signal<string>('view');
  removingUser = signal<boolean>(false);
  selectedMember = signal<any>(null);
  flowTemplateCategoryId = signal<number>(0);
  tableData = signal<WorkflowPermissionTable[]>([]);
  reloadApiLoader = signal<boolean>(false);
  showID = signal<boolean>(false);
  showDescription = signal<boolean>(true);
  showCategory = signal<boolean>(true);
  workflowId = '';
  workflowDetails = signal<RequestDetailsResponse>(workflowDetailsInitialData);
  assigneeTableData = signal<WorkflowPermissionDetailsAssigneeTabList[]>([]);
  assigneeTotalPages = signal<number>(20);
  assigneeTotalCount = signal<string>('');
  assigneeCurrentPage = signal<number>(1);
  assigneeCurrentSize = signal<number>(25);
  isAssigneeLoading = signal<boolean>(false);
  firstLoading = signal<boolean>(true);
  currentRoute = '';

  constructor() {
    this.routeSubscription();
  }

  ngOnInit() {
    this.getWorkflowPermissionsDetails();
    this.getWorkflowPermissionTableData();
    this.getAssigneeList();
  }

  routeSubscription() {
    this.route.params.subscribe((params: any) => {
      this.workflowId = params.permission_id;
      this.mode.set(params.mode);

      if (this.mode() === VIEW_MODE) {
        this.getWorkflowPermissionsDetails();
        this.getWorkflowPermissionTableData();
      }
    });
  }

  getWorkflowPermissionsDetails(firstLoad = false) {
    firstLoad ? this.firstLoading.set(true) : this.isLoading.set(true);
    this.workflowService
      .getWorkflowPermissionDetails(this.workflowId)
      .subscribe({
        next: (res: any) => {
          this.workflowDetails.set(res);
          this.flowTemplateCategoryId.set(res.data.category.id);
        },
        error: () => {
          this.router.navigateByUrl(
            this.tenantService.adminPath(
              `${ADMIN_ROUTES.WORKFLOW.PERMISSIONS.LIST}`
            )
          );
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }

  getWorkflowPermissionTableData(firstLoad = false) {
    firstLoad ? this.firstLoading.set(true) : this.isLoading.set(true);
    this.workflowService
      .getWorkflowPermissionUsersTable(
        this.workflowId,
        this.currentPage(),
        this.currentSize()
      )
      .subscribe({
        next: (res: any) => {
          this.handleGetWorkflowPermissionTableDataSuccess(res);
        },
        error: () => {
          this.router.navigateByUrl(
            this.tenantService.adminPath(
              `${ADMIN_ROUTES.WORKFLOW.PERMISSIONS.LIST}`
            )
          );
        },
        complete: () => {
          this.reloadApiLoader()
            ? this.isLoading.set(true)
            : this.isLoading.set(false);
          this.firstLoading.set(false);
        },
      });
  }

  handleGetWorkflowPermissionTableDataSuccess(response: any) {
    if (!response.data.length && this.currentPage() > 1) {
      this.isLoading.set(true);
      this.reloadApiLoader.set(true);
      this.currentPage.set(this.currentPage() - 1);
      this.getWorkflowPermissionTableData();
    } else {
      this.reloadApiLoader.set(false);
    }
    this.tableData.set(response.data);
    this.totalPages.set(response.meta.last_page);
    this.totalMembersCount.set(response.meta.total.toString());
  }

  removeMember(member: any) {
    this.removingUser.set(true);
    this.isLoading.set(true);
    this.workflowService
      .removeWorkflowMember(this.workflowId, member.id)
      .subscribe({
        error: () => {
          this.isLoading.set(false);
        },
        complete: () => {
          this.getWorkflowPermissionTableData();
          this.removingUser.set(true);
        },
      });
  }

  handleMemberEmit(event: any) {
    if (event.action == 'remove') {
      this.removeMember(event.selectedMember);
    } else {
      this.selectedMember.set(event.selectedMember);
    }
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getWorkflowPermissionTableData();
  }

  changeSize($event: number) {
    this.currentSize.set($event);
    this.currentPage.set(1);
    this.getWorkflowPermissionTableData();
  }

  handleAssigneePaginationEmitter(event: {
    currentPage: number;
    currentSize: number;
  }) {
    this.assigneeCurrentSize.set(event.currentSize);
    this.assigneeCurrentPage.set(event.currentPage);
    this.getAssigneeList();
  }

  getAssigneeList() {
    this.isAssigneeLoading.set(true);
    this.workflowService
      .getWorkflowAssigneeList(
        this.workflowId,
        this.assigneeCurrentPage(),
        this.assigneeCurrentSize()
      )
      .subscribe({
        next: res => {
          this.handleGetAssigneeListSuccess(res);
        },
        error: () => {
          this.isAssigneeLoading.set(false);
          this.router.navigateByUrl(
            this.tenantService.adminPath(
              `${ADMIN_ROUTES.WORKFLOW.PERMISSIONS.LIST}`
            )
          );
        },
      });
  }

  handleGetAssigneeListSuccess(res: {
    data: WorkflowPermissionDetailsAssigneeTabList[];
    meta: Meta;
  }) {
    this.assigneeTableData.set(res.data);
    this.assigneeTotalPages.set(res.meta.last_page);
    this.assigneeTotalCount.set(res.meta.total.toString());
    this.isAssigneeLoading.set(false);
  }
}
